import { buttonStyle } from 'components/buttons/buttonStyle';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Button } from 'components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { ViewSwitch, ViewSwitchItem } from './ViewSwitch';

type Props<T extends string> = {
    /**
     * The control's own label, e.g. "Group by:". It names the classic switch as is; the new design's button reads
     * it before the grouping shown, and its menu takes it without the colon.
     */
    label: string;
    value: T;
    /** The options this surface offers, already stripped of the groupings it fixes. */
    options: readonly T[];
    /** This option's caption. A callback rather than a map, so each list keeps its own message keys. */
    optionLabel: (option: T) => string;
    onChange: (next: T) => void;
};

/**
 * The `Group by:` control at the right of the quick-filter row. Grouping is not a filter — it changes
 * the order rows are fetched in — so it sits after the filter controls rather than among them.
 * In the new design it is one toolbar text button, like Columns beside it, reading the label and the grouping
 * shown ("Group by: Date") and opening a menu that checks it.
 */
export function GroupBySelector<T extends string>({
    label,
    value,
    options,
    optionLabel,
    onChange,
}: Props<T>): JSX.Element {
    const isNewDesign = useIsNewDesign();

    if (isNewDesign) {
        // ponytail: the labels end in a colon, which the button keeps before its value; the menu's name drops it.
        const name = label.replace(/:\s*$/, '');
        return (
            <DropdownMenu>
                <DropdownMenuTrigger
                    render={
                        <Button
                            {...buttonStyle(
                                { variant: 'outlined' },
                                'text-foreground-secondary text-meta h-8 border-0 bg-transparent px-2.5 font-medium shadow-none'
                            )}
                        />
                    }
                >
                    {label} <span className="text-foreground font-semibold">{optionLabel(value)}</span>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-auto min-w-44">
                    <DropdownMenuRadioGroup aria-label={name} value={value} onValueChange={(next: T) => onChange(next)}>
                        {options.map((option) => (
                            <DropdownMenuRadioItem
                                key={option}
                                value={option}
                                closeOnClick
                                className="data-checked:bg-primary-wash [&_[data-slot=dropdown-menu-radio-item-indicator]]:text-primary h-[34px] rounded-md px-2.5 pr-8 text-sm data-checked:font-semibold"
                            >
                                {optionLabel(option)}
                            </DropdownMenuRadioItem>
                        ))}
                    </DropdownMenuRadioGroup>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    }

    return (
        <div className="flex items-center gap-2">
            <span className="text-fg-secondary whitespace-nowrap">{label}</span>
            <ViewSwitch value={value} onChange={onChange} aria-label={label}>
                {options.map((option) => (
                    <ViewSwitchItem key={option} value={option}>
                        {optionLabel(option)}
                    </ViewSwitchItem>
                ))}
            </ViewSwitch>
        </div>
    );
}
